import React, { useState, useEffect, useMemo, useRef } from 'react';
import api, { portfolioLabAPI, portfolioRegistryAPI } from '../../../services/api';

const round2 = (n) => Math.round(n * 100) / 100;
const STRATEGIC = 'Strategic Series';
const BAR_COLOURS = ['#072040', '#599BE3', '#f0a500', '#91CF9B', '#874564', '#127080', '#9B59B6', '#E67E22'];

// Normalise a portfolio's holdings to [{fund_code, fund_name, weight, asset_class}] regardless
// of source. The option key encodes the source + reference: `lab:<id>`, `model:<code>`,
// `saa:<code>`. Weights are percentages (0–100) in every source, so they blend directly.
async function fetchHoldingsByKey(key) {
  if (!key) return [];
  const i = key.indexOf(':');
  const source = key.slice(0, i);
  const ref = key.slice(i + 1);
  if (source === 'lab') {
    const d = await portfolioLabAPI.getPortfolio(ref);
    return (d.holdings || []).map((h) => ({
      fund_code: h.fund_code, fund_name: h.fund_name || h.fund_code, weight: h.weight || 0, asset_class: null,
    }));
  }
  if (source === 'model') {
    // Shadow_Data holdings: one row per fund, each with a weights map keyed by portfolio code.
    const res = await api.get(`/api/portfolio/portfolios/holdings?portfolio_codes=${encodeURIComponent(ref)}`);
    const out = [];
    (res.data.holdings || []).forEach((h) => {
      const w = (h.weights || {})[ref];
      if (w == null) return;
      const code = h.tr_code || h.asset_name;   // cash and unlisted lines can lack a TR code
      if (!code) return;
      out.push({ fund_code: code, fund_name: h.asset_name || code, weight: w, asset_class: h.asset_class_group || null });
    });
    return out;
  }
  if (source === 'saa') {
    const res = await portfolioLabAPI.getSAAHoldings([ref]);   // null vintage = live SAA
    const p = (res.portfolios || {})[ref];
    return (p?.holdings || []).map((h) => ({
      fund_code: h.fund_code, fund_name: h.fund_name || h.fund_code, weight: h.weight || 0, asset_class: h.asset_class || null,
    }));
  }
  return [];
}

// Blend any number of portfolios into one. The blended weight of each fund is the sum-product
// of its weight in each portfolio and that portfolio's blend share:
//   blended_weight(fund) = Σ_p  share_p × weight_in_p(fund)
// Sources: your saved (Lab) portfolios, registry model portfolios, and SAA portfolios —
// bucketed by master (suite). The result loads into the builder to tweak and save.
const PortfolioBlend = ({ savedPortfolios = [], onLoadBlend }) => {
  const [registryData, setRegistryData] = useState(null);
  const [saaPortfolios, setSaaPortfolios] = useState([]);
  const [holdingsByKey, setHoldingsByKey] = useState({});   // key -> holdings[]
  const [loadingKeys, setLoadingKeys] = useState({});       // key -> bool
  const [assetClasses, setAssetClasses] = useState({});
  const [error, setError] = useState('');

  // Blend rows: each is one portfolio + its blend weight (%). Starts with two empty rows;
  // the id counter picks up after them (mutated only in event handlers, never during render).
  const rowIdRef = useRef(2);
  const [rows, setRows] = useState([
    { id: 1, key: '', weight: 0 },
    { id: 2, key: '', weight: 0 },
  ]);

  // Load the model (registry) and SAA portfolio lists.
  useEffect(() => {
    portfolioRegistryAPI.getRegistry('portfolio_lab').then(setRegistryData).catch(() => {});
    portfolioLabAPI.getSAAPortfolios().then((d) => setSaaPortfolios(d.portfolios || [])).catch(() => {});
  }, []);

  const modelPortfolios = useMemo(() => {
    if (!registryData?.suites) return [];
    return registryData.suites.flatMap((suite) =>
      (suite.portfolios || []).map((p) => ({
        code: p.portfolio_code, name: p.display_name || p.portfolio_code, suite: suite.label || 'Other',
      })));
  }, [registryData]);

  // Selectable options, bucketed by master (Strategic Series first), each holding that
  // master's model then SAA portfolios, with saved portfolios (grouped by client) last.
  const buckets = useMemo(() => {
    const out = [];
    const masters = new Map();   // suite -> { model: [], saa: [] }
    const bucket = (s) => { if (!masters.has(s)) masters.set(s, { model: [], saa: [] }); return masters.get(s); };
    modelPortfolios.forEach((p) => bucket(p.suite).model.push({ key: `model:${p.code}`, name: `${p.name} (Model)` }));
    saaPortfolios.forEach((p) => bucket(p.suite || 'Other').saa.push({ key: `saa:${p.code}`, name: `${p.display_name || p.code} (SAA)` }));
    [...masters.keys()]
      .sort((a, b) => (a === STRATEGIC ? -1 : b === STRATEGIC ? 1 : a.localeCompare(b)))
      .forEach((s) => {
        const { model, saa } = masters.get(s);
        out.push({ label: s, options: [...model, ...saa] });
      });
    if (savedPortfolios.length) {
      // Saved portfolios grouped by client, alphabetically, with the no-client group last.
      const byClient = new Map();
      savedPortfolios.forEach((p) => {
        const c = (p.client && p.client.trim()) || '';
        if (!byClient.has(c)) byClient.set(c, []);
        byClient.get(c).push({ key: `lab:${p.id}`, name: p.name });
      });
      [...byClient.keys()]
        .sort((a, b) => (a === '' ? 1 : b === '' ? -1 : a.localeCompare(b)))
        .forEach((c) => {
          out.push({ label: c ? `Saved · ${c}` : 'Saved portfolios (no client)', options: byClient.get(c) });
        });
    }
    return out;
  }, [savedPortfolios, modelPortfolios, saaPortfolios]);

  const optByKey = useMemo(() => {
    const m = new Map();
    buckets.forEach((b) => b.options.forEach((o) => m.set(o.key, o)));
    return m;
  }, [buckets]);
  const nameOf = (key) => optByKey.get(key)?.name || '';

  const selectedKeys = useMemo(() => [...new Set(rows.map((r) => r.key).filter(Boolean))], [rows]);
  const selectedKeySet = useMemo(() => new Set(selectedKeys), [selectedKeys]);

  // Fetch holdings for any newly-selected portfolio (cached by key).
  useEffect(() => {
    const missing = selectedKeys.filter((k) => !(k in holdingsByKey));
    if (missing.length === 0) return undefined;
    let stale = false;
    missing.forEach((k) => {
      setLoadingKeys((prev) => ({ ...prev, [k]: true }));
      fetchHoldingsByKey(k)
        .then((h) => { if (!stale) setHoldingsByKey((prev) => ({ ...prev, [k]: h })); })
        .catch(() => { if (!stale) { setHoldingsByKey((prev) => ({ ...prev, [k]: [] })); setError('Failed to load a portfolio'); } })
        .finally(() => { if (!stale) setLoadingKeys((prev) => ({ ...prev, [k]: false })); });
    });
    return () => { stale = true; };
  }, [selectedKeys, holdingsByKey]);

  // Asset classes for any funds whose source didn't provide one (Lab / SAA fund codes are
  // TR codes, so the AP List lookup resolves them).
  useEffect(() => {
    const codes = [...new Set(selectedKeys.flatMap((k) => holdingsByKey[k] || [])
      .filter((h) => !h.asset_class).map((h) => h.fund_code).filter(Boolean))];
    if (codes.length === 0) return undefined;
    let stale = false;
    portfolioLabAPI.assetClassLookup(codes)
      .then((r) => { if (!stale) setAssetClasses((prev) => ({ ...prev, ...(r.lookup || {}) })); })
      .catch(() => {});
    return () => { stale = true; };
  }, [selectedKeys, holdingsByKey]);

  const addRow = () => setRows((prev) => [...prev, { id: ++rowIdRef.current, key: '', weight: 0 }]);
  const removeRow = (id) => setRows((prev) => prev.filter((r) => r.id !== id));
  const updateRow = (id, field, value) => setRows((prev) => prev.map((r) => {
    if (r.id !== id) return r;
    if (field === 'weight') return { ...r, weight: Math.max(0, Math.min(100, parseFloat(value) || 0)) };
    return { ...r, [field]: value };
  }));
  const evenSplit = () => setRows((prev) => {
    const withKey = prev.filter((r) => r.key);
    if (withKey.length === 0) return prev;
    const each = round2(100 / withKey.length);
    const lastId = withKey[withKey.length - 1].id;
    let used = 0;
    return prev.map((r) => {
      if (!r.key) return r;
      const w = r.id === lastId ? round2(100 - used) : each;
      used += each;
      return { ...r, weight: w };
    });
  });

  // Merged fund list — the sum-product blend across every row with a portfolio and weight.
  const merged = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      if (!r.key || !r.weight) return;
      const holdings = holdingsByKey[r.key];
      if (!holdings) return;
      const share = r.weight / 100;
      holdings.forEach((h) => {
        if (!h.fund_code) return;
        const cur = map.get(h.fund_code) || {
          fund_code: h.fund_code, fund_name: h.fund_name || h.fund_code, asset_class: null, weight: 0,
        };
        cur.weight += (h.weight || 0) * share;
        if (h.fund_name) cur.fund_name = h.fund_name;
        if (!cur.asset_class) cur.asset_class = h.asset_class || assetClasses[h.fund_code] || null;
        map.set(h.fund_code, cur);
      });
    });
    return [...map.values()]
      .map((f) => ({ ...f, weight: round2(f.weight) }))
      .filter((f) => Math.abs(f.weight) > 0.001)
      .sort((a, b) => b.weight - a.weight);
  }, [rows, holdingsByKey, assetClasses]);

  const groups = useMemo(() => {
    const g = {};
    merged.forEach((f) => { const ac = f.asset_class || 'Other'; (g[ac] = g[ac] || []).push(f); });
    return Object.entries(g)
      .map(([ac, funds]) => ({ ac, funds, total: funds.reduce((s, f) => s + f.weight, 0) }))
      .sort((a, b) => b.total - a.total);
  }, [merged]);

  const total = useMemo(() => merged.reduce((s, f) => s + f.weight, 0), [merged]);
  const blendTotal = rows.reduce((s, r) => s + (r.key ? (r.weight || 0) : 0), 0);
  const loading = selectedKeys.some((k) => loadingKeys[k]);
  const activeRows = rows.filter((r) => r.key && r.weight > 0);
  const weightsOk = Math.abs(blendTotal - 100) <= 0.5;
  const ready = activeRows.length >= 1 && weightsOk && !loading && merged.length > 0;

  const renderOptionsFor = (currentKey) => buckets.map((b) => {
    const opts = b.options.filter((o) => o.key === currentKey || !selectedKeySet.has(o.key));
    if (opts.length === 0) return null;
    return (
      <optgroup key={b.label} label={b.label}>
        {opts.map((o) => <option key={o.key} value={o.key}>{o.name}</option>)}
      </optgroup>
    );
  }).filter(Boolean);

  const summary = activeRows.map((r) => `${r.weight}% ${nameOf(r.key)}`).join(' + ');

  return (
    <div className="panel blend-panel">
      <div className="panel-header">
        <h5>Blend Portfolios</h5>
      </div>
      <div className="blend-body">
        <p className="blend-help">
          Combine two or more portfolios into a new one, from your saved portfolios, the model
          portfolios, or the SAA portfolios (grouped by master). Set each one&apos;s blend weight;
          the weights must total 100%. Each fund&apos;s weight becomes the sum-product of its
          weight in each portfolio and its blend weight. The result loads into the builder.
        </p>

        {/* Stacked blend bar */}
        {activeRows.length > 0 && (
          <div className="blend-stack">
            {rows.filter((r) => r.key && r.weight > 0).map((r, i) => (
              <div
                key={r.id}
                className="blend-stack-seg"
                style={{ width: `${r.weight}%`, background: BAR_COLOURS[i % BAR_COLOURS.length] }}
                title={`${nameOf(r.key)}: ${r.weight}%`}
              >
                {r.weight >= 8 && <span>{r.weight}%</span>}
              </div>
            ))}
            {blendTotal < 100 && (
              <div className="blend-stack-rest">{round2(100 - blendTotal)}% unallocated</div>
            )}
          </div>
        )}

        {/* Row builder */}
        <table className="blend-rows">
          <thead>
            <tr>
              <th>Portfolio</th>
              <th className="w-col">Weight (%)</th>
              <th className="x-col" />
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id}>
                <td>
                  <span className="blend-swatch" style={{ background: r.key ? BAR_COLOURS[i % BAR_COLOURS.length] : 'transparent' }} />
                  <select
                    className="form-select form-select-sm blend-row-select"
                    value={r.key}
                    onChange={(e) => updateRow(r.id, 'key', e.target.value)}
                  >
                    <option value="">Select a portfolio…</option>
                    {renderOptionsFor(r.key)}
                  </select>
                  {r.key && loadingKeys[r.key] && <span className="blend-row-loading">loading…</span>}
                </td>
                <td className="w-col">
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    min={0}
                    max={100}
                    step={1}
                    value={r.weight || ''}
                    placeholder="0"
                    onChange={(e) => updateRow(r.id, 'weight', e.target.value)}
                  />
                </td>
                <td className="x-col">
                  {rows.length > 1 && (
                    <button className="btn-remove" title="Remove" onClick={() => removeRow(r.id)}>×</button>
                  )}
                </td>
              </tr>
            ))}
            <tr className={`blend-rows-total ${weightsOk ? 'ok' : 'off'}`}>
              <td>Total</td>
              <td className="w-col">{blendTotal.toFixed(1)}%</td>
              <td className="x-col" />
            </tr>
          </tbody>
        </table>

        <div className="blend-row-actions">
          <button className="btn btn-outline-secondary btn-sm" onClick={addRow}>+ Add portfolio</button>
          <button className="btn btn-outline-secondary btn-sm" onClick={evenSplit} disabled={activeRows.length === 0}>
            Even split
          </button>
          {!weightsOk && activeRows.length > 0 && (
            <span className="blend-warn">Weights must total 100% (currently {blendTotal.toFixed(1)}%)</span>
          )}
        </div>

        {error && <div className="alert alert-danger py-2" style={{ fontSize: '0.85rem' }}>{error}</div>}

        {ready && (
          <>
            <div className="blend-preview">
              <table className="holdings-table">
                <thead>
                  <tr>
                    <th className="name-col">Fund Name</th>
                    <th className="code-col">Code</th>
                    <th className="weight-col">Weight (%)</th>
                  </tr>
                </thead>
                <tbody>
                  {groups.map(({ ac, funds, total: acTotal }) => (
                    <React.Fragment key={ac}>
                      <tr className="asset-class-header">
                        <td colSpan={2}>{ac}</td>
                        <td className="weight-col asset-class-total">{acTotal.toFixed(1)}%</td>
                      </tr>
                      {funds.map((f) => (
                        <tr key={f.fund_code} className="holding-row">
                          <td className="name-col">{f.fund_name}</td>
                          <td className="code-col">{f.fund_code}</td>
                          <td className="weight-col">{f.weight.toFixed(2)}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                  <tr className="total-row">
                    <td colSpan={2}>Total ({merged.length} funds)</td>
                    <td className="weight-col">{total.toFixed(1)}%</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="blend-actions">
              <span className="blend-summary">{summary}</span>
              <button
                className="btn"
                style={{ background: 'var(--primary-color)', color: 'var(--white-color)' }}
                onClick={() => onLoadBlend(merged)}
              >
                Load into Builder
              </button>
            </div>
          </>
        )}

        {activeRows.length > 0 && weightsOk && loading && (
          <div className="empty-state"><p className="mb-0">Loading holdings…</p></div>
        )}
      </div>
    </div>
  );
};

export default PortfolioBlend;
